/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Error handling", description = "Understand how Hightouch handles events that fail to write to your warehouse.", lastUpdatedDate = new Date(1782432000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Error handling",
        description: "Understand how Hightouch handles events that fail to write to your warehouse.",
        lastUpdatedDate: "Fri Jun 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Viewing sync errors",
          "depth": 0,
          "id": "viewing-sync-errors"
        }, {
          "value": "Retrying data",
          "depth": 0,
          "id": "retrying-data"
        }, {
          "value": "Fixing column types",
          "depth": 1,
          "id": "fixing-column-types"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      img: "img",
      h3: "h3",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Sometimes Hightouch can't write an event to your warehouse. This can happen\nfor a variety of reasons; for example, the warehouse credentials may be\ninvalid, or your event schema may have evolved to have incompatible types.\nWhen it happens, Hightouch retries the event and stores unresolved failures so you can replay them later."
      }), "\n", _jsx(_components.h2, {
        id: "viewing-sync-errors",
        children: "Viewing sync errors"
      }), "\n", _jsx(_components.p, {
        children: "Sync errors can be found under the sync run page."
      }), "\n", _jsx(_components.p, {
        children: "The run summary page provides a quick summary of the types of errors that are\nkeeping your data from being synced."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/error-summary.png",
          alt: "Error summary screenshot"
        })
      }), "\n", _jsx(_components.p, {
        children: "For each error, you can see how many events it's affecting, as well as\nindividual examples of bad events."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/sample-error-payload.png",
          alt: "Sample error payload screenshot"
        })
      }), "\n", _jsx(_components.h2, {
        id: "retrying-data",
        children: "Retrying data"
      }), "\n", _jsx(_components.p, {
        children: "Invalid data is automatically retried for at least 48 hours. If the data issue\nhas still not been resolved, the invalid events are stored so that they can be\nreplayed at a later date."
      }), "\n", _jsx(_components.p, {
        children: "Replaying events is done via the \"Archived Failures\" screen."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/archived-failures.png",
          alt: "Archived failures screenshot"
        })
      }), "\n", _jsx(_components.h3, {
        id: "fixing-column-types",
        children: "Fixing column types"
      }), "\n", _jsx(_components.p, {
        children: "To change an existing column type, you can create a temporary version of the\ncolumn with the new format."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, the following query may used on Snowflake to change the ", _jsx(_components.code, {
          children: "price"
        }), "\ncolumn on the ", _jsx(_components.code, {
          children: "product_ordered"
        }), " table from an ", _jsx(_components.code, {
          children: "INT"
        }), " to a ", _jsx(_components.code, {
          children: "FLOAT"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "BEGIN"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALTER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TABLE"
          }), " product_ordered ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ADD"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "COLUMN"
          }), " price_float ", _jsx(_components.span, {
            className: "hljs-type",
            children: "FLOAT"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "UPDATE"
          }), " product_ordered ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SET"
          }), " price_float", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "price;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALTER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TABLE"
          }), " product_ordered ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "DROP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "COLUMN"
          }), " price;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALTER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TABLE"
          }), " product_ordered RENAME ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "COLUMN"
          }), " price_float ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " price;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "COMMIT"
          }), ";\n"]
        })
      })]
    });
  }
}
export default MDXContent;
